/**
 * Copyright 2023 Design Barn Inc.
 */

'use client';

import type { ReadonlyURLSearchParams} from 'next/navigation';
import { useSearchParams } from 'next/navigation';
import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { parse } from 'tldts';

import type { ISessionResponse } from '../utils/api-requests';
import { createCallbackEncodedState } from '../utils/oauth-helpers';

const whitelistedDomains = ['lottiefiles.dev', 'lottiefiles.io', 'lottiefiles.com', 'localhost'];

if (!process.env['NEXT_PUBLIC_DEIMOS_SESSION_URL']) {
  throw new Error('NEXT_PUBLIC_DEIMOS_SESSION_URL  environment variable is not set');
}

export interface CallbackResponse {
  errors?: string[];
  success: boolean;
}

export interface IframeMessagingConfig {
  authUrl?: string;
  locale?: string;
  metadata?: string;
  origin?: string;
  returnOnError?: string;
  returnToPath?: string;
  returnToUrl?: string | null;
  targetElementId?: string;
  token?: string;
}

type LoginMethod = 'password' | 'social' | 'enterprise-sso' | 'unknown';
type SocialLoginProvider = 'google' | 'facebook' | 'github' | 'dribbble';

interface IframeMessage {
  authUrl?: string;
  message:
    | 'afterLogin'
    | 'afterLogout'
    | 'afterRegister'
    | 'closeModal'
    | 'openAuthUrl'
    | 'sessionCheckResult'
    | 'logout';
  payload?: unknown;
}

interface IframeMessageWithLoginMethod extends IframeMessage {
  message: 'afterLogin' | 'afterRegister';
  payload: {
    loginMethod: LoginMethod;
    metadata?: string | undefined;
    provider?: SocialLoginProvider;
  };
}

export interface IframeMessagingContextValue {
  afterLogin: (
    loginMethod: LoginMethod,
    provider?: SocialLoginProvider,
    metadata?: string | undefined,
  ) => Promise<CallbackResponse>;
  afterLogout: () => Promise<CallbackResponse>;
  afterRegister: (
    loginMethod: LoginMethod,
    provider?: SocialLoginProvider,
    metadata?: string | undefined,
  ) => Promise<CallbackResponse>;
  closeModal: (options?: { userInitiated: boolean }) => Promise<void>;
  // the params are grouped under a config object as they are used together and easier to pass around
  config: IframeMessagingConfig;
  errorMessage: string | null;
  logout: () => Promise<CallbackResponse>;
  openAuthUrlInParent: (authUrl: string) => Promise<CallbackResponse>;
  sessionCheckResult: (session: ISessionResponse | null) => Promise<void>;
  setErrorMessage: (message: string | null) => unknown;
}

const IframeMessagingContext = createContext<IframeMessagingContextValue>({
  afterLogin: async () => Promise.resolve({ success: false }),
  afterLogout: async () => Promise.resolve({ success: false }),
  afterRegister: async () => Promise.resolve({ success: false }),
  closeModal: async () => Promise.resolve(),
  config: {},
  openAuthUrlInParent: async () => Promise.resolve({ success: false }),
  setErrorMessage: async () => Promise.resolve(),
  errorMessage: null,
  sessionCheckResult: async () => Promise.resolve(),
  logout: async () => Promise.resolve({ success: false }),
});

export const isDomainWhitelisted = (url: string): boolean => {
  // we need to extract the hostname domain for validation
  // because a fuzzy string match would allow malicious urls
  // like https://lottiefiles.com.evil.com or https://badwebsite.com/?lottiefiles.com
  const parseResult = parse(url);

  // this case handles for localhost, because it doesn't have a domain
  if (!parseResult.domain && parseResult.hostname) {
    return whitelistedDomains.includes(parseResult.hostname);
  }

  if (parseResult.domain && typeof parseResult.domain === 'string') {
    return whitelistedDomains.includes(parseResult.domain);
  }

  return false;
};

export interface IframeConfigValidationFailure {
  field: 'origin' | 'returnToUrl' | 'returnToPath';
  reason: string;
  value: string | undefined;
}

/**
 * Validates the iframe input config and returns a structured failure when invalid,
 * or `null` when valid. Each failure carries the offending field, its value, and a
 * human-readable reason so the implementer can act on the specific problem instead
 * of guessing.
 *
 * Callers that only need a boolean can use `isIframeConfigValid`.
 */
export const validateIframeConfig = (config: IframeMessagingConfig): IframeConfigValidationFailure | null => {
  const { origin, returnToPath, returnToUrl } = config;
  const allowed = whitelistedDomains.join(', ');

  if (!origin) {
    return {
      field: 'origin',
      value: undefined,
      reason:
        'Missing required "origin" query param. The auth-client must include `origin=<parent-page-url>` in the iframe URL — check that the parent page actually called auth-client (load/login/register) before this iframe was loaded.',
    };
  }

  if (!isDomainWhitelisted(origin)) {
    return {
      field: 'origin',
      value: origin,
      reason: `"origin" (${origin}) is not a whitelisted domain. The parent page hosting auth-client must run on one of: ${allowed}. For local development, use a subdomain of lottiefiles.dev (e.g. something.local.lottiefiles.dev) served over HTTPS on port 443 — see packages/auth-client/techdocs/index.md.`,
    };
  }

  if (returnToUrl && returnToUrl.length > 0 && !isDomainWhitelisted(returnToUrl)) {
    return {
      field: 'returnToUrl',
      value: returnToUrl,
      reason: `"returnToUrl" (${returnToUrl}) is not a whitelisted domain. Allowed: ${allowed}.`,
    };
  }

  if (!returnToPath) {
    return {
      field: 'returnToPath',
      value: undefined,
      reason:
        'Missing required "return_to_path" query param. Pass a non-empty `fullPageReturnToPath` to auth-client `load()` (e.g. `window.location.pathname`) or a `returnToPath`/`returnToUrl` option to `login()`/`register()`.',
    };
  }

  const fullReturnUrl = `${origin}${returnToPath}`;

  if (!isDomainWhitelisted(fullReturnUrl)) {
    return {
      field: 'returnToPath',
      value: returnToPath,
      reason: `Composite return URL "${fullReturnUrl}" (built from origin + returnToPath) is not whitelisted. This usually means returnToPath embeds a domain switcher (e.g. ".badsite.com/path"). Allowed: ${allowed}.`,
    };
  }

  return null;
};

export const isIframeConfigValid = (config: IframeMessagingConfig): boolean => validateIframeConfig(config) === null;

const getIframeInputConfig = (params: ReadonlyURLSearchParams): IframeMessagingConfig => {
  const escapedOrigin = params.get('origin');
  const escapedReturnToPath = params.get('return_to_path');
  const escapedReturnToUrl = params.get('return_to_url');
  const escapedReturnOnError = params.get('return_on_error');
  const escapedTargetElementId = params.get('target_element_id');
  const metadata = params.get('metadata');

  const token = params.get('token');
  const locale = params.get('locale');

  return {
    ...(escapedOrigin && { origin: decodeURIComponent(escapedOrigin) }),
    ...(escapedReturnToPath && { returnToPath: escapedReturnToPath }),
    ...(escapedReturnToUrl && { returnToUrl: escapedReturnToUrl }),
    ...(escapedReturnOnError && { returnOnError: escapedReturnOnError }),
    ...(escapedTargetElementId && { targetElementId: decodeURIComponent(escapedTargetElementId) }),
    ...(metadata && { metadata: decodeURIComponent(metadata) }),
    token: token || '',
    locale: locale || 'en',
  };
};

export const IframeMessagingProvider = ({ children }: { children: React.ReactNode }): React.ReactElement => {
  const searchParams = useSearchParams();
  const [currentConfig, setCurrentConfig] = useState<IframeMessagingConfig>({});
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    // source of truth for state will always be loca lStorage
    const localStorageState = localStorage.getItem('iframe-messaging-state');

    const { locale, metadata, origin, returnOnError, returnToPath, returnToUrl, targetElementId, token } =
      getIframeInputConfig(searchParams);

    // load state from local storage if exists, and  merge input config in
    const newConfig = {
      ...(localStorageState ? (JSON.parse(localStorageState) as IframeMessagingConfig) : {}),
      ...(origin && { origin }),
      ...(returnToPath && { returnToPath }),
      ...(token && { token }),
      targetElementId,
      metadata,
      returnToUrl,
      returnOnError,
      locale,
    };

    // need to check here after local storage is loa ded to avoid
    // local storage being manipulated by user befor e this check
    const validationFailure = validateIframeConfig(newConfig);

    if (validationFailure) {
      // Log structured diagnostics to the console so the implementer can see exactly
      // what came in over the URL, what was merged from localStorage, and which field
      // failed. The thrown Error message also includes the reason so it's visible in
      // overlays / Bugsnag without needing to open DevTools.
      // eslint-disable-next-line no-console
      console.error('[auth-iframe] Invalid iframe input config.', {
        field: validationFailure.field,
        value: validationFailure.value,
        reason: validationFailure.reason,
        receivedConfig: newConfig,
        urlSearchParams: Object.fromEntries(searchParams.entries()),
        localStorageState: localStorageState ? (JSON.parse(localStorageState) as IframeMessagingConfig) : null,
        whitelistedDomains,
      });

      throw new Error(
        `Invalid iframe input config — ${validationFailure.field}: ${validationFailure.reason}`,
      );
    }

    localStorage.setItem('iframe-messaging-state', JSON.stringify(newConfig));

    setCurrentConfig(newConfig);

    const incomingError = searchParams.get('error_message');

    if (incomingError) {
      setErrorMessage(incomingError);
    }

    // check if theme was saved in localstorage
  }, [searchParams, setCurrentConfig]);

  const openAuthUrlInParent = useCallback(
    async (authUrl: string): Promise<CallbackResponse> => {
      const iframeMessage: IframeMessage = {
        message: 'openAuthUrl',
        authUrl,
      };

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (window && window.top && window.top.postMessage) {
        window.top.postMessage(iframeMessage, currentConfig.origin || window.location.origin);
      }

      return {
        success: true,
      };
    },
    [currentConfig.origin],
  );

  const afterLogin = useCallback(
    async (
      loginMethod: LoginMethod,
      provider?: SocialLoginProvider,
      metadata?: string | undefined,
    ): Promise<CallbackResponse> => {
      if (!currentConfig.origin) {
        return {
          success: false,
          errors: [
            'Internal error: no parent "origin" is set on the current iframe config. The parent frame cannot be notified because postMessage requires a target origin. This usually means the iframe was loaded without an `origin` query param.',
          ],
        };
      }

      const iframeMessage: IframeMessageWithLoginMethod = {
        message: 'afterLogin',
        payload: {
          loginMethod,
          provider,
          metadata,
        },
      };

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (window && window.top && window.top.postMessage) {
        window.top.postMessage(iframeMessage, currentConfig.origin);
      }

      return {
        success: true,
      };
    },
    [currentConfig.origin],
  );

  const afterLogout = useCallback(async (): Promise<CallbackResponse> => {
    if (!currentConfig.origin) {
      return {
        success: false,
        errors: [
            'Internal error: no parent "origin" is set on the current iframe config. The parent frame cannot be notified because postMessage requires a target origin. This usually means the iframe was loaded without an `origin` query param.',
          ],
      };
    }

    const iframeMessage: IframeMessage = {
      message: 'afterLogout',
    };

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (window && window.top && window.top.postMessage) {
      window.top.postMessage(iframeMessage, currentConfig.origin);
    }

    return {
      success: true,
    };
  }, [currentConfig.origin]);

  const logout = useCallback(async (): Promise<CallbackResponse> => {
    if (!currentConfig.origin) {
      return {
        success: false,
        errors: [
            'Internal error: no parent "origin" is set on the current iframe config. The parent frame cannot be notified because postMessage requires a target origin. This usually means the iframe was loaded without an `origin` query param.',
          ],
      };
    }

    const iframeMessage: IframeMessage = {
      message: 'logout',
    };

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (window && window.top && window.top.postMessage) {
      window.top.postMessage(iframeMessage, currentConfig.origin);
    }

    return {
      success: true,
    };
  }, [currentConfig.origin]);

  const afterRegister = useCallback(
    async (
      loginMethod: LoginMethod,
      provider?: SocialLoginProvider,
      metadata?: string | undefined,
    ): Promise<CallbackResponse> => {
      if (!currentConfig.origin) {
        return {
          success: false,
          errors: [
            'Internal error: no parent "origin" is set on the current iframe config. The parent frame cannot be notified because postMessage requires a target origin. This usually means the iframe was loaded without an `origin` query param.',
          ],
        };
      }

      const iframeMessage: IframeMessageWithLoginMethod = {
        message: 'afterRegister',
        payload: {
          loginMethod,
          provider,
          metadata,
        },
      };

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (window && window.top && window.top.postMessage) {
        window.top.postMessage(iframeMessage, currentConfig.origin);
      }

      return {
        success: true,
      };
    },
    [currentConfig.origin],
  );

  const closeModal = useCallback(
    async (options?: { userInitiated: boolean } | undefined) => {
      const { origin } = getIframeInputConfig(searchParams);

      const userInitiated = options?.userInitiated;

      if (!origin) {
        throw new Error(
          'Invalid origin endpoint — closeModal was called but no "origin" query param is present in the iframe URL. The auth-client must include `origin=<parent-page-url>` when opening this iframe.',
        );
      }

      const iframeMessage: IframeMessage = {
        message: 'closeModal',
        ...(userInitiated && { payload: { userInitiated: true } }),
      };

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (window && window.top && window.top.postMessage) {
        window.top.postMessage(iframeMessage, origin);
      }

      return Promise.resolve();
    },
    [searchParams],
  );

  const sessionCheckResult = useCallback(
    async (session: ISessionResponse | null) => {
      if (!currentConfig.origin) {
        throw new Error(
          'Invalid origin endpoint — sessionCheckResult was called but no parent "origin" is set on the current iframe config. This usually means the iframe was loaded without an `origin` query param.',
        );
      }
      const iframeMessage = {
        message: 'sessionCheckResult',
        session,
        googleCallbackUrl: process.env['NEXT_PUBLIC_GOOGLE_REDIRECT_URL'],
        googleCallbackState: createCallbackEncodedState({
          config: currentConfig,
          returnAction: 'login',
          returnTo: currentConfig.returnToPath || '',
          locale: currentConfig.locale || 'en',
        }),
        googleClientId: process.env['NEXT_PUBLIC_GOOGLE_CLIENT_ID'],
      };

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (window && window.top && window.top.postMessage) {
        window.top.postMessage(iframeMessage, currentConfig.origin);
      }
    },
    [currentConfig],
  );

  const contextValue = useMemo<IframeMessagingContextValue>(
    () => ({
      afterLogin,
      afterLogout,
      afterRegister,
      closeModal,
      logout,
      config: currentConfig,
      openAuthUrlInParent,
      errorMessage,
      setErrorMessage,
      sessionCheckResult,
    }),
    [
      afterLogin,
      afterLogout,
      afterRegister,
      closeModal,
      currentConfig,
      logout,
      openAuthUrlInParent,
      errorMessage,
      setErrorMessage,
      sessionCheckResult,
    ],
  );

  return <IframeMessagingContext.Provider value={contextValue}>{children}</IframeMessagingContext.Provider>;
};

export const useIframeMessaging = (): IframeMessagingContextValue => React.useContext(IframeMessagingContext);
